/**
 * A note on lined paper (the intro letter from Mags, the online welcome): a few short pages, turned with
 * Enter / Space / E / → / a tap, skipped with Esc or SKIP. Not a menu: the game keeps running behind it
 * (dimmed), it just has the player's attention for a few seconds.
 */
import { el, toggle } from '../ui/dom';

export interface NotePage {
  /** Small print above the heading ("A note, pushed under your door"). */
  kicker: string;
  heading: string;
  lines: string[];
  /** Signed at the foot of the page. */
  sign?: string;
  /** Short list in typewriter (what you can do online). */
  bullets?: string[];
}

export class NoteCard {
  readonly root: HTMLElement;
  page = 0;
  private readonly sheet: HTMLElement;
  private readonly body: HTMLElement;
  private readonly nextBtn: HTMLElement;
  private readonly pips: HTMLElement[] = [];
  private done = false;

  constructor(
    parent: HTMLElement,
    private readonly pages: readonly NotePage[],
    private readonly touch: boolean,
    private readonly onTurn: () => void,
    private readonly onDone: (skipped: boolean) => void,
    cls = '',
  ) {
    this.root = el('div', `ob-note ${cls}`.trim(), undefined, parent);
    this.root.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.skip')) return;
      this.next();
    });
    this.sheet = el('div', 'sheet', undefined, this.root);
    this.body = el('div', 'body', undefined, this.sheet);
    const foot = el('div', 'foot', undefined, this.sheet);
    const pips = el('span', 'pips', undefined, foot);
    for (let i = 0; i < pages.length; i++) this.pips.push(el('i', '', undefined, pips));
    const skip = el('button', 'skip', 'Skip', foot);
    skip.type = 'button';
    skip.addEventListener('click', (e) => {
      e.stopPropagation();
      this.finish(true);
    });
    this.nextBtn = el('button', 'next', '', foot);
    (this.nextBtn as HTMLButtonElement).type = 'button';
    this.render();
  }

  private render(): void {
    const p = this.pages[this.page]!;
    this.body.textContent = '';
    el('div', 'kicker', p.kicker, this.body);
    el('h2', '', p.heading, this.body);
    for (const l of p.lines) el('p', '', l, this.body);
    if (p.bullets?.length) {
      const ul = el('ul', '', undefined, this.body);
      for (const b of p.bullets) el('li', '', b, ul);
    }
    if (p.sign) el('div', 'sign', p.sign, this.body);
    this.pips.forEach((e, i) => toggle(e, 'on', i === this.page));
    const last = this.page === this.pages.length - 1;
    this.nextBtn.innerHTML = this.touch ? (last ? 'Tap to start ▸' : 'Tap to turn over ▸') : `${last ? 'Start' : 'Turn over'} <kbd>Enter</kbd>`;
    this.sheet.classList.remove('turn');
    void this.sheet.offsetWidth;
    this.sheet.classList.add('turn');
  }

  next(): void {
    if (this.done) return;
    if (this.page >= this.pages.length - 1) {
      this.finish(false);
      return;
    }
    this.page++;
    this.onTurn();
    this.render();
  }

  finish(skipped: boolean): void {
    if (this.done) return;
    this.done = true;
    this.root.classList.add('leaving');
    window.setTimeout(() => this.root.remove(), 380);
    this.onDone(skipped);
  }

  get open(): boolean {
    return !this.done;
  }

  dispose(): void {
    this.done = true;
    this.root.remove();
  }
}
